<template>
  <div id="knowledgeCollection">
    <div class="collection-container">
      <!-- 左侧分组列表 -->
      <div class="left-panel">
        <div class="group-header">
          <div class="header-title">分组列表</div>
          <div class="header-actions">
            <el-dropdown trigger="click" @command="handleCreateCommand">
              <i class="el-icon-plus" title="添加"></i>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="group">创建分组</el-dropdown-item>
                <el-dropdown-item command="task">创建任务</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
        <!-- 分组搜索框 -->
        <div class="group-search-container">
          <el-input
            v-model="groupSearchKeyword"
            placeholder="搜索分组名称"
            clearable
            prefix-icon="el-icon-search"
            size="small"
            @input="handleGroupSearch"
          ></el-input>
        </div>
        <div class="group-list-container">
          <div class="group-list">
            <div
              v-for="group in filteredGroups"
              :key="group.id"
              class="group-item"
              :class="{ 'active': selectedGroupId === group.id }"
              @click="selectGroup(group.id)"
              @mouseenter="hoveredGroupId = group.id"
              @mouseleave="hoveredGroupId = null"
            >
              <i class="iconfont guoran-wenjianjia"></i>
              <span class="group-name">{{ group.name }}</span>
              <span class="group-count">({{ getGroupTaskCount(group) }})</span>
              <el-button
                v-if="hoveredGroupId === group.id"
                type="text"
                icon="el-icon-delete"
                class="group-delete-btn"
                size="small"
                @click.stop="handleDeleteGroup(group)"
                title="删除分组"
              ></el-button>
            </div>
            <div v-if="filteredGroups.length === 0" class="empty-groups">
              <p v-if="groupSearchKeyword">未找到匹配的分组</p>
              <p v-else>暂无分组，请创建分组</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 右侧内容区域 -->
      <div class="right-panel">
        <!-- 任务卡片列表（选中分组时显示） -->
        <div v-if="selectedGroupId" class="task-cards-container">
          <div class="task-cards-header">
            <h3>{{ getSelectedGroupName() }}</h3>
            <div class="task-header-actions">
              <el-input
                v-model="taskSearchKeyword"
                placeholder="搜索任务名称或URL"
                clearable
                prefix-icon="el-icon-search"
                size="small"
                style="width: 240px; margin-right: 12px;"
                @input="handleTaskSearch"
              ></el-input>
              <el-button type="primary" size="small" @click="handleCreateCommand('task')">
                <i class="el-icon-plus"></i> 创建任务
              </el-button>
              <el-button type="default" size="small" @click="openAllCollectionResults">
                <i class="el-icon-document"></i> 采集结果
              </el-button>
            </div>
          </div>
          <div class="task-cards-list">
            <div
              v-for="task in filteredTasks"
              :key="task.id"
              class="task-card"
              @mouseenter="hoveredTaskId = task.id"
              @mouseleave="hoveredTaskId = null"
            >
              <div class="task-card-header">
                <el-input
                  v-if="editingTaskId === task.id"
                  v-model="editingTaskName"
                  size="small"
                  class="task-name-input"
                  @blur="saveTaskName(task)"
                  @keyup.enter.native="saveTaskName(task)"
                  @keyup.esc.native="cancelEditTaskName"
                  ref="taskNameInput"
                  @click.stop
                ></el-input>
                <h4 v-else class="task-card-title" @click="openTaskDetail(task.id)">{{ task.name }}</h4>
                <div class="task-card-header-right">
                  <el-button
                    v-if="hoveredTaskId === task.id && editingTaskId !== task.id"
                    type="text"
                    icon="el-icon-edit"
                    class="task-edit-btn"
                    size="small"
                    @click.stop="startEditTaskName(task)"
                    title="编辑名称"
                  ></el-button>
                  <el-button
                    v-if="hoveredTaskId === task.id && editingTaskId !== task.id"
                    type="text"
                    icon="el-icon-delete"
                    class="task-delete-btn"
                    size="small"
                    @click.stop="handleDeleteTask(task)"
                    title="删除任务"
                  ></el-button>
                </div>
              </div>
              <div class="task-card-content" @click="openTaskDetail(task.id)">
                <div class="task-card-info" v-if="getTaskPrimaryUrl(task)">
                  <i class="el-icon-link"></i>
                  <span class="task-url">{{ getTaskPrimaryUrl(task) }}</span>
                  <span v-if="getTaskInputUrls(task).length > 1" class="task-url-count">
                    等{{ getTaskInputUrls(task).length }}条
                  </span>
                </div>
                <div class="task-card-meta">
                  <span v-if="task.createTime" class="task-time">
                    <i class="el-icon-time"></i>
                    {{ formatTime(task.createTime) }}
                  </span>
                  <el-tag v-if="task.status" :type="getTaskStatusType(task.status)" size="mini">
                    {{ getTaskStatusText(task.status) }}
                  </el-tag>
                </div>
              </div>
            </div>
            <div v-if="filteredTasks.length === 0" class="empty-tasks">
              <i class="el-icon-document"></i>
              <p v-if="taskSearchKeyword">未找到匹配的任务</p>
              <p v-else>该分组下暂无任务</p>
              <el-button type="primary" size="small" @click="handleCreateCommand('task')">
                创建任务
              </el-button>
            </div>
          </div>
        </div>
        
        <!-- 空状态（未选择分组时） -->
        <div v-else class="empty-state-container">
          <div class="empty-state">
            <i class="el-icon-folder-opened"></i>
            <p>请选择左侧的分组查看任务</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 创建分组对话框 -->
    <el-dialog
      title="创建分组"
      :visible.sync="createGroupDialogVisible"
      width="500px"
      @close="resetGroupForm"
    >
      <el-form :model="groupForm" label-width="100px">
        <el-form-item label="名称">
          <el-input v-model="groupForm.name" placeholder="请输入分组名称"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createGroupDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreateGroup">确定</el-button>
      </div>
    </el-dialog>
    
    <!-- 创建任务弹窗（网址导入） -->
    <popup-com
      v-if="createTaskDialogVisible"
      @closeEvent="closeCreateTaskDialog"
    >
      <template slot="popup-name">创建任务</template>
      <template slot="popup-tip"></template>
      <template slot="popup-con">
        <div class="task-create-form">
          <div class="form-row">
            <div class="form-item">
              <label class="form-label">任务分组</label>
              <el-select v-model="taskForm.groupId" placeholder="请选择分组" style="width: 100%">
                <el-option
                  v-for="group in taskGroups"
                  :key="group.id"
                  :label="group.name"
                  :value="group.id"
                ></el-option>
              </el-select>
            </div>
            <div class="form-item">
              <label class="form-label">网站类型</label>
              <el-select v-model="taskForm.siteType" placeholder="请选择网站类型" style="width: 100%">
                <el-option label="普通网站" value="普通网站"></el-option>
                <el-option label="微信公众号" value="微信公众号"></el-option>
                <el-option label="搜索" value="搜索"></el-option>
              </el-select>
            </div>
          </div>
          <!-- 普通网站和搜索：显示任务名称选择 -->
          <div v-if="taskForm.siteType === '普通网站' || taskForm.siteType === '搜索'">
            <div class="form-row">
              <div class="form-item">
                <label class="form-label">任务名称</label>
                <el-select v-model="taskForm.nameType" placeholder="请选择" style="width: 100%">
                  <el-option label="使用网页标题" value="webpage"></el-option>
                  <el-option label="自定义" value="custom"></el-option>
                </el-select>
              </div>
            </div>
            <div class="form-row" v-if="taskForm.nameType === 'custom'">
              <div class="form-item">
                <label class="form-label">自定义名称</label>
                <el-input
                  v-model="taskForm.customName"
                  placeholder="请输入任务名称"
                  style="width: 100%"
                ></el-input>
              </div>
            </div>
          </div>
          <!-- 普通网站：显示网址输入 -->
          <div class="url-import-section" v-if="taskForm.siteType === '普通网站'">
            <div class="section-title">网址导入</div>
            <div class="url-input-area">
              <div class="input-tips">
                <p>请输入要采集的网址，不同网址需要回车换行分隔</p>
              </div>
              <el-input
                v-model="taskForm.urls"
                type="textarea"
                :rows="4"
                placeholder="请输入要采集的网址，例如：https://www.askbot.cn/tag/itsm/"
                class="url-input"
              ></el-input>
            </div>
          </div>
          <!-- 微信公众号：显示公众号搜索 -->
          <div class="url-import-section" v-if="taskForm.siteType === '微信公众号'">
            <div class="section-title">公众号选择</div>
            <div class="url-input-area">
              <div class="input-tips">
                <p>请输入公众号名称进行搜索</p>
              </div>
              <el-autocomplete
                v-model="taskForm.wechatAccountSearchKeyword"
                :fetch-suggestions="searchWechatAccount"
                :trigger-on-focus="false"
                placeholder="请输入公众号名称"
                class="url-input"
                value-key="name"
                @select="handleWechatAccountSelect"
                :loading="taskForm.searchingWechatAccount"
                style="width: 100%"
              >
                <template slot-scope="{ item }">
                  <div class="wechat-account-option">
                    <span class="account-name">{{ item.name }}</span>
                  </div>
                </template>
              </el-autocomplete>
              <div v-if="taskForm.selectedWechatAccount" class="selected-account-info" style="margin-top: 12px; padding: 12px; background-color: #f5f7fa; border-radius: 4px;">
                <span style="color: #606266;">已选择公众号：</span>
                <span style="color: #303133; font-weight: 500;">{{ taskForm.selectedWechatAccount.name }}</span>
              </div>
            </div>
          </div>
          <!-- 搜索：显示网址输入 -->
          <div class="url-import-section" v-if="taskForm.siteType === '搜索'">
            <div class="section-title">网址导入</div>
            <div class="url-input-area">
              <div class="input-tips">
                <p>请输入搜索页面的网址，可多行或用逗号分隔，创建后可在配置页面设置搜索参数</p>
              </div>
              <el-input
                v-model="taskForm.urls"
                type="textarea"
                :rows="4"
                placeholder="请输入搜索页面的网址，例如：https://www.example.com/search"
                class="url-input"
              ></el-input>
            </div>
          </div>
        </div>
      </template>
      <template slot="dialog-footer">
        <el-button @click="closeCreateTaskDialog" class="cancel-btn" :disabled="createTaskLoading">取消</el-button>
        <el-button type="primary" @click="handleCreateTask" class="confirm-btn" :loading="createTaskLoading">立即创建</el-button>
      </template>
    </popup-com>
  </div>
</template>

<script>
import PopupCom from "@/components/popup";
import { requestUrl } from "@/http/requestUrl";

// 缓存key
const CACHE_KEY_SELECTED_GROUP_ID = 'knowledgeCollection_selectedGroupId';

export default {
  name: "knowledgeCollection",
  components: {
    PopupCom
  },
  data() {
    return {
      taskGroups: [],
      allTaskGroups: [], // 保存所有分组（用于搜索）
      selectedGroupId: null,
      createGroupDialogVisible: false,
      createTaskDialogVisible: false,
      groupForm: {
        name: ''
      },
      taskForm: {
        groupId: null,
        nameType: 'webpage',
        customName: '',
        urls: '',
        siteType: '普通网站', // 网站类型：普通网站、微信公众号 或 搜索
        selectedWechatAccount: null, // 选中的公众号
        wechatAccountSearchKeyword: '', // 公众号搜索关键词
        wechatAccountSearchResults: [], // 公众号搜索结果
        searchingWechatAccount: false // 是否正在搜索公众号
      },
      groupSearchKeyword: '', // 分组搜索关键词
      taskSearchKeyword: '', // 任务搜索关键词
      hoveredGroupId: null, // 当前悬停的分组ID
      hoveredTaskId: null, // 当前悬停的任务ID
      allTasks: [], // 保存当前分组的所有任务（用于搜索）
      createTaskLoading: false, // 创建任务loading状态
      editingTaskId: null, // 当前正在编辑的任务ID
      editingTaskName: '' // 编辑中的任务名称
    }
  },
  computed: {
    // 过滤后的分组列表
    filteredGroups() {
      if (!this.groupSearchKeyword) {
        return this.taskGroups
      }
      const keyword = this.groupSearchKeyword.toLowerCase()
      return this.taskGroups.filter(group => {
        const name = (group.name || '').toLowerCase()
        return name.includes(keyword)
      })
    },
    // 过滤后的任务列表
    filteredTasks() {
      if (!this.taskSearchKeyword) {
        return this.allTasks
      }
      const keyword = this.taskSearchKeyword.toLowerCase()
      return this.allTasks.filter(task => {
        const name = (task.name || '').toLowerCase()
        const urls = this.getTaskInputUrls(task)
        const matchedUrl = urls.some(urlItem => urlItem.toLowerCase().includes(keyword))
        return name.includes(keyword) || matchedUrl
      })
    }
  },
  watch: {
    selectedGroupId(newId) {
      if (newId) {
        this.loadTaskList(newId)
      } else {
        this.allTasks = []
      }
      // 重置任务搜索关键词
      this.taskSearchKeyword = ''
    },
    'taskForm.siteType'(newType) {
      // 切换网站类型时，重置相关字段
      if (newType === '微信公众号') {
        // 切换到微信公众号时，清空网址和任务名称相关字段
        this.taskForm.urls = ''
        this.taskForm.nameType = 'webpage'
        this.taskForm.customName = ''
      } else if (newType === '普通网站') {
        // 切换到普通网站时，清空公众号相关字段
        this.taskForm.selectedWechatAccount = null
        this.taskForm.wechatAccountSearchKeyword = ''
        this.taskForm.wechatAccountSearchResults = []
      } else if (newType === '搜索') {
        // 切换到搜索时，清空其他类型相关字段
        this.taskForm.nameType = 'webpage'
        this.taskForm.customName = ''
        this.taskForm.selectedWechatAccount = null
        this.taskForm.wechatAccountSearchKeyword = ''
        this.taskForm.wechatAccountSearchResults = []
      }
    }
  },
  methods: {
    loadGroupList() {
      return this.$http.get(requestUrl.collection.groupList).then(res => {
        if (res.data.code === '0') {
          const groups = res.data.data || []
          const groupPromises = groups.map(group => {
            // 使用API返回的任务数量，而不是重新加载任务列表
            return Promise.resolve({
              ...group,
              taskCount: group.taskCount || 0,
              tasks: [] // 初始化为空，需要时再加载
            })
          })
          return Promise.all(groupPromises).then(list => {
            this.allTaskGroups = list
            this.taskGroups = list
            // 应用搜索过滤
            this.handleGroupSearch()
            
            // 优先从缓存中恢复选中的分组
            const cachedGroupId = this.getCachedGroupId()
            let targetGroupId = null
            
            if (list.length > 0) {
              if (cachedGroupId) {
                // 检查缓存的分组ID是否在列表中有效（需要处理类型转换，因为ID可能是数字或字符串）
                const cachedGroup = list.find(g => {
                  // 转换为字符串进行比较，确保类型匹配
                  return String(g.id) === String(cachedGroupId)
                })
                if (cachedGroup) {
                  // 缓存的分组存在，优先使用缓存的分组（使用原始ID，保持类型一致）
                  targetGroupId = cachedGroup.id
                } else {
                  // 缓存的分组不存在，使用第一个分组并更新缓存
                  targetGroupId = list[0].id
                  this.setCachedGroupId(String(targetGroupId))
                }
              } else {
                // 没有缓存，使用当前选中的分组或第一个分组
                targetGroupId = this.selectedGroupId || list[0].id
                // 如果当前没有选中分组，保存第一个分组到缓存
                if (!this.selectedGroupId) {
                  this.setCachedGroupId(String(targetGroupId))
                }
              }
              
              // 设置选中的分组（无论当前值是什么，都使用计算出的targetGroupId）
              if (targetGroupId) {
                this.selectedGroupId = targetGroupId
                // watch 会自动触发 loadTaskList
              }
            }
            
            if (!this.taskForm.groupId && list.length > 0) {
              this.taskForm.groupId = this.selectedGroupId || list[0].id
            }
            return list
          })
        }
        this.$message.error(res.data.message || '加载分组列表失败')
        return []
      }).catch(error => {
        console.error('加载分组列表错误:', error)
        return []
      })
    },
    loadTaskList(groupId) {
      if (!groupId) {
        this.allTasks = []
        return Promise.resolve([])
      }
      return this.$http.get(requestUrl.collection.taskList, {
        params: { groupId }
      }).then(res => {
        if (res.data.code === '0') {
          const tasks = res.data.data || []
          const normalizedTasks = tasks.map(task => this.normalizeTaskInputUrls(task))
          this.allTasks = normalizedTasks
          // 更新对应分组的任务列表
          const group = this.taskGroups.find(g => g.id === groupId)
          if (group) {
            group.tasks = normalizedTasks
          }
          return normalizedTasks
        }
        this.allTasks = []
        return []
      }).catch(error => {
        console.error('加载任务列表错误:', error)
        this.allTasks = []
        return []
      })
    },
    selectGroup(groupId) {
      this.selectedGroupId = groupId
      // 保存到缓存
      this.setCachedGroupId(groupId)
    },
    // 获取缓存的分组ID
    getCachedGroupId() {
      try {
        return sessionStorage.getItem(CACHE_KEY_SELECTED_GROUP_ID)
      } catch (e) {
        console.error('读取分组缓存失败:', e)
        return null
      }
    },
    // 设置缓存的分组ID
    setCachedGroupId(groupId) {
      try {
        if (groupId) {
          // 统一转换为字符串存储
          sessionStorage.setItem(CACHE_KEY_SELECTED_GROUP_ID, String(groupId))
        } else {
          sessionStorage.removeItem(CACHE_KEY_SELECTED_GROUP_ID)
        }
      } catch (e) {
        console.error('保存分组缓存失败:', e)
      }
    },
    openTaskDetail(taskId) {
      // 获取当前任务信息
      const task = this.getSelectedGroupTasks().find(t => t.id === taskId)
      
      // 根据任务状态决定进入哪个tab
      let tab = 'config' // 默认进入配置页
      
      if (task) {
        const status = task.status
        
        // 如果已完成，进入采集结果tab
        if (status === 'completed') {
          tab = 'results'
        }
        // 如果正在采集中或已暂停，进入采集详情tab
        else if (status === 'collecting' || status === 'paused') {
          tab = 'progress'
        }
        // 如果是新任务（没有status或status为空），进入配置tab
        // 其他状态（failed, stopped等）也进入配置tab
        else {
          tab = 'config'
        }
      }
      
      this.$router.push({
        name: 'taskDetail',
        params: { taskId },
        query: { tab }
      })
    },
    // 打开所有采集结果页面
    openAllCollectionResults() {
      try {
        this.$router.push({
          name: 'allCollectionResults'
        })
      } catch (error) {
        console.error('路由跳转错误:', error)
        // 如果使用路由名称失败，尝试使用路径
        this.$router.push('/knowledge_collection/results')
      }
    },
    getSelectedGroupName() {
      if (!this.selectedGroupId) return ''
      const group = this.taskGroups.find(g => g.id === this.selectedGroupId)
      return group ? group.name : ''
    },
    getSelectedGroupTasks() {
      return this.allTasks
    },
    getGroupTaskCount(group) {
      // 优先使用API返回的任务数量
      if (group.taskCount !== undefined && group.taskCount !== null) {
        return group.taskCount
      }
      // 如果没有，使用tasks数组长度
      return group.tasks ? group.tasks.length : 0
    },
    // 分组搜索处理
    handleGroupSearch() {
      // 搜索逻辑在computed中处理
    },
    // 任务搜索处理
    handleTaskSearch() {
      // 搜索逻辑在computed中处理
    },
    // 删除分组
    handleDeleteGroup(group) {
      this.$confirm(`确定要删除分组"${group.name}"吗？删除后该分组下的任务将保留。`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const url = requestUrl.collection.groupDelete + group.id
        this.$http.delete(url)
          .then(res => {
            if (res.data.code === '0') {
              this.$message.success('分组删除成功')
              // 如果删除的是当前选中的分组，清空选中状态
              if (this.selectedGroupId === group.id) {
                this.selectedGroupId = null
                this.allTasks = []
                // 清除缓存
                this.setCachedGroupId(null)
              }
              // 重新加载分组列表
              this.loadGroupList()
            } else {
              this.$message.error(res.data.message || '分组删除失败')
            }
          })
          .catch(error => {
            this.$message.error('分组删除失败，请稍后重试')
            console.error('删除分组错误:', error)
          })
      }).catch(() => {
        // 用户取消删除
      })
    },
    // 删除任务
    handleDeleteTask(task) {
      this.$confirm(`确定要删除任务"${task.name}"吗？此操作不可恢复。`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const url = requestUrl.collection.taskDelete + task.id
        this.$http.delete(url)
          .then(res => {
            if (res.data.code === '0') {
              this.$message.success('任务删除成功')
              // 从任务列表中移除
              this.allTasks = this.allTasks.filter(t => t.id !== task.id)
              // 更新分组中的任务列表
              const group = this.taskGroups.find(g => g.id === this.selectedGroupId)
              if (group) {
                group.tasks = group.tasks.filter(t => t.id !== task.id)
                // 更新任务数量
                if (group.taskCount !== undefined) {
                  group.taskCount = Math.max(0, (group.taskCount || 0) - 1)
                }
              }
              // 更新分组列表中的任务数量
              const allGroups = this.allTaskGroups || this.taskGroups
              const groupInAll = allGroups.find(g => g.id === this.selectedGroupId)
              if (groupInAll) {
                if (groupInAll.taskCount !== undefined) {
                  groupInAll.taskCount = Math.max(0, (groupInAll.taskCount || 0) - 1)
                }
              }
              // 重新加载分组列表以同步任务数量
              this.loadGroupList()
            } else {
              this.$message.error(res.data.message || '任务删除失败')
            }
          })
          .catch(error => {
            this.$message.error('任务删除失败，请稍后重试')
            console.error('删除任务错误:', error)
          })
      }).catch(() => {
        // 用户取消删除
      })
    },
    // 开始编辑任务名称
    startEditTaskName(task) {
      this.editingTaskId = task.id
      this.editingTaskName = task.name
      this.$nextTick(() => {
        const inputs = this.$refs.taskNameInput
        if (inputs) {
          const input = Array.isArray(inputs) ? inputs[0] : inputs
          if (input && input.focus) {
            input.focus()
            input.select()
          }
        }
      })
    },
    // 保存任务名称
    saveTaskName(task) {
      const newName = (this.editingTaskName || '').trim()
      if (!newName) {
        this.$message.warning('任务名称不能为空')
        this.editingTaskName = task.name
        return
      }
      if (newName === task.name) {
        this.cancelEditTaskName()
        return
      }
      // 调用接口保存任务名称
      this.$http.put(requestUrl.collection.taskUpdate + task.id, {
        name: newName
      }).then(res => {
        if (res.data.code === '0') {
          // 更新本地数据
          task.name = newName
          this.$message.success('任务名称已更新')
        } else {
          this.$message.error(res.data.message || '更新失败')
        }
      }).catch(error => {
        console.error('更新任务名称失败:', error)
        this.$message.error('更新失败，请稍后重试')
      }).finally(() => {
        this.cancelEditTaskName()
      })
    },
    // 取消编辑任务名称
    cancelEditTaskName() {
      this.editingTaskId = null
      this.editingTaskName = ''
    },
    getTaskStatusType(status) {
      const typeMap = {
        collecting: 'warning',
        completed: 'success',
        failed: 'danger',
        paused: 'info',
        stopped: 'info'
      }
      return typeMap[status] || 'info'
    },
    getTaskStatusText(status) {
      const textMap = {
        collecting: '采集中',
        completed: '已完成',
        failed: '已失败',
        paused: '已暂停',
        stopped: '已停止'
      }
      return textMap[status] || '未知'
    },
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    handleCreateCommand(command) {
      if (command === 'group') {
        this.createGroupDialogVisible = true
      } else if (command === 'task') {
        this.createTaskDialogVisible = true
        if (this.selectedGroupId) {
          this.taskForm.groupId = this.selectedGroupId
        } else if (this.taskGroups.length > 0) {
          this.taskForm.groupId = this.taskGroups[0].id
        }
      }
    },
    handleCreateGroup() {
      if (!this.groupForm.name.trim()) {
        this.$message.warning('请输入分组名称')
        return
      }
      this.$http.post(requestUrl.collection.groupCreate, {
        name: this.groupForm.name
      }).then(res => {
        if (res.data.code === '0') {
          this.$message.success('分组创建成功')
          this.createGroupDialogVisible = false
          this.resetGroupForm()
          this.loadGroupList()
        } else {
          this.$message.error(res.data.message || '分组创建失败')
        }
      }).catch(error => {
        this.$message.error('分组创建失败，请稍后重试')
        console.error('创建分组错误:', error)
      })
    },
    resetGroupForm() {
      this.groupForm = {
        name: ''
      }
    },
    // 搜索公众号
    async searchWechatAccount(queryString, cb) {
      if (!queryString || !queryString.trim()) {
        cb([])
        return
      }
      this.taskForm.searchingWechatAccount = true
      try {
        const res = await this.$http.request({
          method: 'GET',
          url: requestUrl.collection.websiteList,
          params: { name: queryString.trim() }
        })
        if (res.data.code === '0') {
          const results = res.data.data || []
          this.taskForm.wechatAccountSearchResults = results
          // 如果搜索结果为空，提示用户
          if (results.length === 0) {
            // 延迟提示，避免在输入过程中频繁提示
            setTimeout(() => {
              if (this.taskForm.wechatAccountSearchResults.length === 0 && queryString.trim()) {
                this.$message.warning('该公众号暂未支持采集，请联系AskBot工作人员')
              }
            }, 500)
          }
          cb(results)
        } else {
          cb([])
        }
      } catch (error) {
        console.error('搜索公众号失败:', error)
        cb([])
      } finally {
        this.taskForm.searchingWechatAccount = false
      }
    },
    // 选择公众号
    handleWechatAccountSelect(item) {
      this.taskForm.selectedWechatAccount = item
      this.taskForm.wechatAccountSearchKeyword = item.name
    },
    handleCreateTask() {
      if (!this.taskForm.groupId) {
        this.$message.warning('请选择任务分组')
        return
      }
      // 如果是普通网站或搜索且选择自定义名称，需要验证名称是否填写
      if ((this.taskForm.siteType === '普通网站' || this.taskForm.siteType === '搜索') && this.taskForm.nameType === 'custom') {
        if (!this.taskForm.customName || !this.taskForm.customName.trim()) {
          this.$message.warning('请输入自定义任务名称')
          return
        }
      }
      // 根据网站类型进行不同的验证
      let taskData = {}
      if (this.taskForm.siteType === '普通网站') {
        const urls = this.parseInputUrls(this.taskForm.urls)
        if (!urls.length) {
          this.$message.warning('请输入要采集的网址')
          return
        }
        const invalidUrl = urls.find(url => !this.isValidUrl(url))
        if (invalidUrl) {
          this.$message.warning('请输入有效的网址')
          return
        }
        taskData = {
          url: urls[0],
          urls,
          siteType: 'website'
        }
        // 如果选择自定义，添加名称字段
        if (this.taskForm.nameType === 'custom' && this.taskForm.customName.trim()) {
          taskData.name = this.taskForm.customName.trim()
        }
      } else if (this.taskForm.siteType === '微信公众号') {
        if (!this.taskForm.selectedWechatAccount) {
          // 检查是否搜索过但没有结果
          if (this.taskForm.wechatAccountSearchKeyword && this.taskForm.wechatAccountSearchResults.length === 0) {
            this.$message.warning('该公众号暂未支持采集，请联系AskBot工作人员')
          } else {
            this.$message.warning('请搜索并选择公众号')
          }
          return
        }
        // 微信公众号任务：需要 siteType、siteId 和公众号名称作为任务名称
        const selectedAccount = this.taskForm.selectedWechatAccount;
        const siteId = selectedAccount.siteId || selectedAccount.site_id;
        
        taskData = {
          url: 'https://mp.weixin.qq.com', // 微信公众号固定URL
          siteType: 'wechat',
          siteId: siteId,
          name: selectedAccount.name // 使用公众号名称作为任务名称
        }
      } else if (this.taskForm.siteType === '搜索') {
        // 搜索任务：需要验证URL
        const urls = this.parseInputUrls(this.taskForm.urls)
        if (!urls.length) {
          this.$message.warning('请输入搜索页面的网址')
          return
        }
        const invalidUrl = urls.find(url => !this.isValidUrl(url))
        if (invalidUrl) {
          this.$message.warning('请输入有效的网址')
          return
        }
        // 搜索任务：只需要URL和siteType，其他配置在配置页面完成
        taskData = {
          url: urls[0],
          urls,
          siteType: 'search'
        }
        // 如果选择自定义，添加名称字段
        if (this.taskForm.nameType === 'custom' && this.taskForm.customName.trim()) {
          taskData.name = this.taskForm.customName.trim()
        }
      }
      const tasks = [taskData]
      this.createTaskLoading = true
      this.$http.post(requestUrl.collection.taskCreate, {
        groupId: this.taskForm.groupId,
        tasks
      }, {
        timeout: 300000 // 5分钟超时
      }).then(res => {
        if (res.data.code === '0') {
          this.$message.success(`成功创建${res.data.data.createdCount}个任务`)
          this.closeCreateTaskDialog()
          this.loadGroupList().then(() => {
            if (res.data.data.tasks && res.data.data.tasks.length > 0) {
              const firstTask = res.data.data.tasks[0]
              this.openTaskDetail(firstTask.id)
            }
            this.createTaskLoading = false
          }).catch(() => {
            this.createTaskLoading = false
          })
        } else {
          this.$message.error(res.data.message || '任务创建失败')
          this.createTaskLoading = false
        }
      }).catch(error => {
        console.error('创建任务错误:', error)
        this.handleCreateTaskTimeoutFallback(taskData)
      })
    },
    resetTaskForm() {
      this.taskForm = {
        groupId: this.selectedGroupId || (this.taskGroups.length > 0 ? this.taskGroups[0].id : null),
        nameType: 'webpage',
        customName: '',
        urls: '',
        siteType: '普通网站',
        selectedWechatAccount: null,
        wechatAccountSearchKeyword: '',
        wechatAccountSearchResults: [],
        searchingWechatAccount: false
      }
    },
    normalizeTaskInputUrls(task) {
      if (!task) {
        return task
      }
      const inputUrls = this.getTaskInputUrls(task)
      return {
        ...task,
        inputUrls
      }
    },
    getTaskInputUrls(task) {
      if (!task) {
        return []
      }
      const collected = []
      const collectString = (value) => {
        if (typeof value !== 'string') {
          return
        }
        this.splitUrlText(value).forEach(item => {
          if (item) {
            collected.push(item)
          }
        })
      }
      const collectArray = (value) => {
        if (!Array.isArray(value)) {
          return
        }
        value.forEach(item => {
          if (typeof item === 'string' && item.trim()) {
            collected.push(item.trim())
          }
        })
      }
      collectArray(task.inputUrls)
      collectArray(task.urls)
      collectString(task.inputUrls)
      collectString(task.urls)
      if (typeof task.url === 'string' && task.url.trim()) {
        collected.push(task.url.trim())
      }
      const unique = []
      const seen = new Set()
      collected.forEach(item => {
        if (!seen.has(item)) {
          seen.add(item)
          unique.push(item)
        }
      })
      return unique
    },
    getTaskPrimaryUrl(task) {
      const urls = this.getTaskInputUrls(task)
      return urls[0] || ''
    },
    splitUrlText(value) {
      if (!value) {
        return []
      }
      return String(value)
        .split(/[\n,]+/)
        .map(item => item.trim())
        .filter(Boolean)
    },
    parseInputUrls(raw) {
      const urls = this.splitUrlText(raw)
      const unique = []
      const seen = new Set()
      urls.forEach(item => {
        if (!seen.has(item)) {
          seen.add(item)
          unique.push(item)
        }
      })
      return unique
    },
    isValidUrl(url) {
      try {
        new URL(url)
        return true
      } catch (e) {
        return false
      }
    },
    closeCreateTaskDialog() {
      this.createTaskDialogVisible = false
      this.resetTaskForm()
    },
    async handleCreateTaskTimeoutFallback(taskData) {
      const groupId = this.taskForm.groupId
      try {
        const tasks = await this.loadTaskList(groupId)
        const createdTask = this.findLikelyCreatedTask(tasks, taskData)
        if (createdTask) {
          this.$message.warning('创建请求超时，任务已在列表中')
          this.closeCreateTaskDialog()
          return
        }
        this.$message.error('任务创建失败，请稍后重试')
      } catch (error) {
        console.error('创建任务后刷新失败:', error)
        this.$message.error('任务创建失败，请稍后重试')
      } finally {
        this.createTaskLoading = false
      }
    },
    findLikelyCreatedTask(tasks, taskData) {
      if (!Array.isArray(tasks) || !taskData) {
        return null
      }
      const name = (taskData.name || '').trim().toLowerCase()
      const urls = this.normalizeUrlListFromTaskData(taskData).map(url => url.toLowerCase())
      const siteId = taskData.siteId
      return tasks.find(task => {
        if (taskData.siteType === '微信公众号') {
          const taskSiteId = task.siteId || task.site_id
          if (siteId && taskSiteId && String(taskSiteId) === String(siteId)) {
            return true
          }
          if (name && (task.name || '').toLowerCase() === name) {
            return true
          }
          return false
        }
        const taskUrls = this.getTaskInputUrls(task).map(url => url.toLowerCase())
        const urlMatched = urls.length > 0 && urls.some(url => taskUrls.includes(url))
        const nameMatched = name && (task.name || '').toLowerCase() === name
        return urlMatched || nameMatched
      }) || null
    },
    normalizeUrlListFromTaskData(taskData) {
      const urls = []
      if (Array.isArray(taskData.urls)) {
        taskData.urls.forEach(item => {
          if (typeof item === 'string' && item.trim()) {
            urls.push(item.trim())
          }
        })
      }
      if (typeof taskData.url === 'string' && taskData.url.trim()) {
        urls.push(taskData.url.trim())
      }
      const unique = []
      const seen = new Set()
      urls.forEach(item => {
        if (!seen.has(item)) {
          seen.add(item)
          unique.push(item)
        }
      })
      return unique
    },
  },
  mounted() {
    // 加载分组列表，loadGroupList中会处理缓存恢复
    this.loadGroupList()
  },
  activated() {
    // 如果使用了keep-alive，在激活时也要检查缓存
    const cachedGroupId = this.getCachedGroupId()
    if (cachedGroupId) {
      // 如果分组列表已加载，检查缓存的分组是否存在
      if (this.taskGroups.length > 0) {
        const cachedGroup = this.taskGroups.find(g => g.id === cachedGroupId)
        if (cachedGroup && cachedGroupId !== this.selectedGroupId) {
          // 如果缓存的分组存在且当前选中的不是它，则切换到缓存的分组
          this.selectedGroupId = cachedGroupId
          this.loadTaskList(cachedGroupId)
        } else if (!cachedGroup) {
          // 如果缓存的分组不存在，清除缓存并使用第一个分组
          this.setCachedGroupId(null)
          if (this.taskGroups.length > 0 && !this.selectedGroupId) {
            this.selectedGroupId = this.taskGroups[0].id
            this.loadTaskList(this.selectedGroupId)
          }
        }
      } else {
        // 如果分组列表未加载，先加载分组列表（loadGroupList会处理缓存恢复）
        this.loadGroupList()
      }
    } else if (!this.selectedGroupId && this.taskGroups.length === 0) {
      // 如果没有缓存且分组列表未加载，加载分组列表
      this.loadGroupList()
    }
  }
}
</script>

<style scoped lang="less">
#knowledgeCollection {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.collection-container {
  flex: 1;
  display: flex;
  background-color: #f5f7fa;
  height: calc(100vh - 150px)
}

.left-panel {
  width: 260px;
  background-color: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
}

.group-header {
  padding: 16px;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;

  i {
    font-size: 18px;
    color: #606266;
    cursor: pointer;

    &:hover {
      color: #366aff;
    }
  }
}

.group-list-container {
  flex: 1;
  padding: 8px 0;
  .group-list{
    height: calc(100vh - 250px);
    overflow-y: auto;
  }
}

.group-search-container {
  padding: 12px 16px;
  border-bottom: 1px solid #e4e7ed;
}

.group-item {
  padding: 12px 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;

  &:hover {
    background-color: #f5f7fa;
  }

  &.active {
    background-color: #e6f0ff;
    color: #366aff;
  }

  .group-name {
    flex: 1;
    margin-left: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .group-count {
    color: #909399;
    font-size: 12px;
    margin-right: 8px;
  }

  .group-delete-btn {
    opacity: 0;
    transition: opacity 0.2s;
    color: #f56c6c;
    padding: 4px 8px;
    
    &:hover {
      color: #f56c6c;
      background-color: #fef0f0;
    }
  }

  &:hover .group-delete-btn {
    opacity: 1;
  }
}

.empty-groups {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.empty-tasks {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: calc(100vh - 250px);
  color: #909399;
  
  i {
    font-size: 64px;
    color: #c0c4cc;
    margin-bottom: 16px;
  }
  
  p {
    margin: 0 0 20px 0;
    font-size: 14px;
    color: #909399;
  }
  
  .el-button {
    margin-top: 8px;
  }
}

.right-panel {
  flex: 1;
  // background-color: #fff;
  overflow-y: auto;
  margin-left: 10px;
}

.task-cards-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.task-cards-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  padding: 16px;
  border-bottom: 1px solid #e4e7ed;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
  }

  .task-header-actions {
    display: flex;
    align-items: center;
  }
}

.task-cards-list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  height: calc(100vh - 200px);
  overflow-y: auto;
  align-content: flex-start;
  width: 100%;
}

.task-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
  flex: 0 0 calc((100% - 32px) / 3);
  height: 160px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 0;
  box-sizing: border-box;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
    border-color: #c0c4cc;
  }
}

.task-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
  overflow: hidden;
  gap: 8px;
  min-width: 0;

  .task-card-header-right {
    display: flex;
    align-items: flex-start;
    gap: 0;
    flex-shrink: 0;
    margin-left: auto;
    padding-top: 2px;
    
    .el-tag {
      white-space: nowrap;
    }

    .task-edit-btn,
    .task-delete-btn {
      opacity: 0;
      transition: opacity 0.2s;
      padding: 4px 4px;
      flex-shrink: 0;
      margin: 0;
    }

    .task-edit-btn {
      color: #409eff;
      
      &:hover {
        color: #409eff;
        background-color: #ecf5ff;
      }
    }

    .task-delete-btn {
      color: #f56c6c;
      
      &:hover {
        color: #f56c6c;
        background-color: #fef0f0;
      }
    }
  }

  .task-name-input {
    flex: 1;
    min-width: 0;

    :deep(.el-input__inner) {
      font-size: 14px;
      font-weight: 600;
      padding: 2px 8px;
      height: 28px;
      line-height: 28px;
    }
  }
}

.task-card:hover .task-card-header-right {
  .task-edit-btn,
  .task-delete-btn {
    opacity: 1;
  }
}

.task-card-title {
  margin: 0;
  font-size: 14px;
  color: #303133;
  word-break: break-all;
  overflow-wrap: break-word;
  word-wrap: break-word;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  cursor: pointer;
}

.task-card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  overflow: hidden;
  min-height: 0;
  justify-content: space-between;
}

.task-card-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #606266;
  font-size: 13px;
  margin-bottom: 0;
  flex-shrink: 0;

  .task-url {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .task-url-count {
    color: #909399;
    font-size: 12px;
    flex-shrink: 0;
  }
}

.task-cards-list .task-card-meta {
  font-size: 12px;
  color: #909399;
  flex-shrink: 0;
  margin-top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  .task-time {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.empty-state-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  text-align: center;
  color: #909399;

  i {
    font-size: 48px;
    margin-bottom: 12px;
  }
}

.task-create-form {
  .form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 24px;

    .form-item {
      flex: 1;

      .form-label {
        display: block;
        font-size: 14px;
        color: #303133;
        margin-bottom: 8px;
      }
    }
  }

  .url-import-section {
    .section-title {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
      margin-bottom: 16px;
    }

    .input-tips {
      margin-bottom: 12px;
      font-size: 12px;
      color: #909399;
    }

    .wechat-account-option {
      .account-name {
        font-size: 14px;
        color: #303133;
      }
    }

    .selected-account-info {
      font-size: 14px;
    }
  }
}
</style>
